import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import DeleteConfirmModal from "../../common/crud/DeleteConfirmModal";
import EntityTableCard from "../../common/crud/EntityTableCard";
import Pen from "../../assets/images/pen.svg";
import DeleteIMG from "../../assets/images/trash.svg";
import useBusiness from "../../hooks/store/business";
import usePermissions from "../../hooks/usePermissions";

export const BusinessTable = ({
  count = 0,
  items = [],
  onPageChange = () => {},
  onRowsPerPageChange,
  page = 0,
  rowsPerPage = 10,
  selected = [],
}) => {
  const navigate = useNavigate();
  const { updateBusinessAction } = useBusiness();
  const { capabilities, canAccessBusiness } = usePermissions();
  const canManageBusinessDirectory = Boolean(
    capabilities?.canCreateBusinesses
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);

  const handleOpenBusiness = useCallback((businessItem) => {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem(
          "lastOpenedBusiness",
          JSON.stringify({
            id: businessItem.id,
            name: businessItem.name,
          })
        );
      }
    } catch (error) {
      console.error(error);
    }

    navigate(`/business/edit?id=${businessItem.id}`);
  }, [navigate]);

  const handleDeleteBusiness = async () => {
    setIsDeleteModalOpen(false);

    await updateBusinessAction({
      id: selectedBusiness?.id,
      expectedRevision: selectedBusiness?.revision,
      status: "Deactive",
    });
  };

  const columns = useMemo(
    () => [
      {
        id: "serial",
        label: "S.No",
        render: (_, { rowNumber }) => rowNumber,
      },
      {
        id: "name",
        label: "Name",
        render: (businessItem) => {
          const canOpenBusiness = canAccessBusiness(businessItem.id);

          return (
            <Typography
              component="span"
              sx={{
                color: canOpenBusiness ? "primary.main" : "text.primary",
                cursor: canOpenBusiness ? "pointer" : "default",
                textDecoration: canOpenBusiness ? "underline" : "none",
                "&:hover": {
                  color: canOpenBusiness ? "primary.dark" : "text.primary",
                },
              }}
              onClick={() => {
                if (canOpenBusiness) {
                  handleOpenBusiness(businessItem);
                }
              }}
            >
              {businessItem.name}
            </Typography>
          );
        },
      },
      {
        id: "client",
        key: "idClient",
        label: "Client",
      },
      {
        id: "website",
        key: "websiteUrl",
        label: "Website",
      },
      {
        id: "address",
        key: "address",
        label: "Address",
      },
      {
        id: "phone",
        key: "phone",
        label: "Phone number",
      },
      canManageBusinessDirectory && {
        id: "action",
        label: "Action",
        render: (businessItem) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box
              component="img"
              src={Pen}
              alt="pen"
              onClick={() => handleOpenBusiness(businessItem)}
              sx={{ cursor: "pointer" }}
            />
            <Box
              component="img"
              src={DeleteIMG}
              alt="DeleteIMG"
              onClick={() => {
                setSelectedBusiness(businessItem);
                setIsDeleteModalOpen(true);
              }}
              sx={{
                cursor: "pointer",
                height: "20px",
              }}
            />
          </Box>
        ),
      },
    ].filter(Boolean),
    [canAccessBusiness, canManageBusinessDirectory, handleOpenBusiness]
  );

  return (
    <>
      <EntityTableCard
        columns={columns}
        count={count}
        headSx={{ position: "sticky", top: 0 }}
        items={items}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        page={page}
        rowsPerPage={rowsPerPage}
        selected={selected}
      />
      <DeleteConfirmModal
        message=" Are you sure you want to delete this business?"
        onCancel={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteBusiness}
        open={isDeleteModalOpen}
      />
    </>
  );
};

BusinessTable.propTypes = {
  count: PropTypes.number,
  items: PropTypes.array,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  selected: PropTypes.array,
};
